import videojs from 'video.js';
import * as API from '../../api';
import { QueryStringParamProvider } from 'PLAYER_SRC/implementations/player-page/helpers.js';

const DEFAULT_PLAYER_COLOR = '#ED8B00';

export class QuizzesPluginStub extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        super(player, options);

        player.loader().onLoadPlayCover(onLoadPlayCover.bind(player));
        player.loader().onLoadPresentation(onLoadPresentation.bind(player));
    }
}

function shouldLoad(presentationId, playCoverData, presentationData) {
    const paramProvider = new QueryStringParamProvider(location.search);
    const previewQuizzes = (paramProvider.getParam('previewQuizzes')?.toLowerCase() === 'true');
    const noQuizzes = (paramProvider.getParam('quizzes')?.toLowerCase() === 'false');
    window.SiteData.QuizResultContextId = paramProvider.getParam('QuizResultContextId')?.toLowerCase();
    const { playerOptions } = presentationData;
    const { quizzes: quizOptions } = playerOptions;

    if (noQuizzes) {
        playCoverData.hasQuizzes = false;
    }

    if ((playCoverData.hasQuizzes && (quizOptions?.enabled !== false)) || previewQuizzes) {
        document.querySelector('.mediasite-standalone-player-container').className += ' has-quizzes';
        return API.Quizzes.getPresentationQuiz(presentationId).then(quizData => quizData?.Quizzes?.length > 0 ? quizData : false);
    }
    else {
        return Promise.reject(false);
    }
}

async function initializeQuizPlugin(quizData, player) {
    if (quizData) {
        if (!videojs.getPlugin('quizzes')) {
            const { QuizzesPlugin } = await import(/* webpackChunkName: 'quizzes' */ './quizzes.js');
            player.registerPlugin('quizzes', QuizzesPlugin);
        }

        player.quizzes().init({ quizData });
    }
}

function onLoadPlayCover(presentationId, playCoverData, presentationData) {
    const player = this;
    return shouldLoad(...arguments).then(async quizData => {
        if (quizData) {
            quizData.applyCustomPlayerColor = playCoverData.customPlayerColor && playCoverData.customPlayerColor !== DEFAULT_PLAYER_COLOR;
            quizData.ResourceId = presentationId;
        }
        await initializeQuizPlugin(quizData, player);
    }, function doesNotHaveQuizzes(noQuizData) { /* Do nothing if no quizzes */ });
}

async function onLoadPresentation(presentationId, playCoverData, presentationData) {
    const player = this;
    if (!playCoverData.poster) {
        playCoverData.hasQuizzes = presentationData.presentationOptions.HasQuizzes;
        await shouldLoad(presentationId, playCoverData, presentationData).then(async quizData => {
            if (quizData) {
                const customColor = presentationData?.playerOptions?.settings?.customPlayerColor;
                quizData.applyCustomPlayerColor = customColor && customColor !== DEFAULT_PLAYER_COLOR;
                quizData.ResourceId = presentationId;
            }
            await initializeQuizPlugin(quizData, player);
        }, function doesNotHaveQuizzes(noQuizData) { /* Do nothing if no quizzes */ });
    }

    if (player.usingPlugin('quizzes')) {
        const { playerOptions } = presentationData;
        const quizTimes = presentationData.timedEvents.filter(event => event.Type === API.Presentation.TimedEventType.PresentationSubQuiz);

        if (quizTimes.length > 0 && player.quizzes) {
            const quizzingBuffer = 50;
            //since quizzes can only be configured to be placed on the exact second, plus two quizzes cannot be the same time
            //we can get away with presuming that, if a quiz is at 0, it is the first quiz and
            //if a quiz is within a second of the end, it is the last quiz
            //having a quiz at exactly 0 means that we can't have the slider move past the starting time, which means it can't trigger on restart or seeking
            if (quizTimes[0].Time == 0) {
                quizTimes[0].Time = quizzingBuffer;
            }

            //Backoff last quiz position if it is in the exact location of the end of the media
            //has to be greater than the duration of the quiz timed event (current 750 ms)
            if (quizTimes[quizTimes.length - 1].Time >= (presentationData.compositionMap.Duration - (750 + quizzingBuffer))) {
                quizTimes[quizTimes.length - 1].Time = presentationData.compositionMap.Duration - (750 + quizzingBuffer);
            }

            var quizResultParams = {
                PresentationId: presentationId,
                PresentationQuizId: player.quizzes().quizData.Id,
                AuthTicketId: window.SiteData.AuthTicket,
                MediasiteChannelId: window.SiteData.CollectionId,
                QuizResultContextId: window.SiteData.QuizResultContextId
            }

            return API.Quizzes.getQuizResults(quizResultParams).then(quizResults => {
                player.quizzes().addQuizzes(quizTimes, quizResults);
                const enableSeeking = !player.quizzes().quizData.DisableSeeking
                return {
                    playerOptions: Object.assign({}, playerOptions, {
                        chapters: Object.assign({}, playerOptions.settings, {
                            showChapterList: enableSeeking
                        }),
                        search: Object.assign({}, playerOptions.settings, {
                            enabled: enableSeeking
                        }),
                        settings: Object.assign({}, playerOptions.settings, {
                            enableSeeking: enableSeeking
                        })
                    })
                };
            });
        }
        else {
            return {
                playerOptions: {
                    quizzes: undefined
                }
            }
        }
    }
}
